import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";

const Section = ({ id, title, children }) => (
  <section className="mb-8" id={id}>
    <h2 className="text-xl md:text-2xl tracking-tight mb-3" data-testid={`privacy-${id}`}>{title}</h2>
    <div className="text-black/75 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

export default function Privacy() {
  const updated = "February 18, 2026";
  return (
    <PageShell
      eyebrow="Privacy"
      title="Privacy policy."
      subtitle={`Last updated: ${updated}. This policy explains what we collect, why we collect it, how long we keep it, and the controls you have.`}
      narrow
    >
      <PageMeta
        title="Privacy policy"
        description="What SendMe collects, why, how long, and your rights. GDPR-compliant, data-minimising, encrypted, no third-party ad trackers outside of AdSense on the blog."
      />
      <div className="max-w-3xl">
        <Section id="intro" title="1. Introduction">
          <p>
            SendMe ("we", "us", "our") is operated from Delhi, India. We provide a file-transfer service at <strong>sendme.co.in</strong>. This privacy policy applies to all visitors and registered users of the service, regardless of where they are based. It explains what personal data we process, the legal bases on which we rely, the rights you have, and how to exercise them.
          </p>
          <p>
            We've written this in plain English on purpose. If anything is unclear, write to <a className="underline" href="mailto:privacy@sendme.co.in">privacy@sendme.co.in</a> and we'll explain.
          </p>
        </Section>

        <Section id="what-we-collect" title="2. What we collect">
          <p>For each file transfer we store:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>The file content itself, until it expires.</li>
            <li>Metadata: filename, size, content-type, expiry timestamp, created-at timestamp.</li>
            <li>An optional bcrypt hash of the password you set (we never store passwords in plain text).</li>
            <li>The 6-digit code we generate to identify the transfer.</li>
          </ul>
          <p>For registered accounts we additionally store:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Email address and display name.</li>
            <li>For email/password sign-up: a bcrypt hash of your account password.</li>
            <li>For Google sign-in: the Google user identifier returned to us by Google.</li>
            <li>For team workspaces: the mobile number you verify, the role you hold, and the workspace identifiers.</li>
            <li>Subscription and billing metadata (plan, status, last-renewal date). Razorpay handles all payment-card data; we never see card numbers.</li>
          </ul>
          <p>We also collect minimal technical telemetry from your browser: IP address (used only for rate limiting and abuse prevention, hashed and rotated after 30 days), user-agent string, and pageview events through Google Analytics 4.</p>
        </Section>

        <Section id="what-we-dont" title="3. What we never collect">
          <p>
            We do not sell your data. We do not run third-party advertising trackers (the only third-party tag on our site, AdSense, only loads on /blog pages, and we serve non-personalised ads to EU visitors by default). We do not browse the content of your files. We do not use your file content to train machine-learning models. We do not share data with brokers.
          </p>
        </Section>

        <Section id="how-we-use" title="4. How we use the data">
          <p>The data we collect is used to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Deliver the file from sender to receiver and authenticate the 6-digit code lookup.</li>
            <li>Enforce expiry, rate limits, and acceptable-use rules.</li>
            <li>Operate the account features you signed up for — transfer history, team workspaces, billing.</li>
            <li>Detect, investigate, and prevent fraud and abuse.</li>
            <li>Comply with legal obligations including taxation, fraud reporting, and law-enforcement orders.</li>
            <li>Improve the service using aggregated, non-identifying analytics.</li>
          </ul>
        </Section>

        <Section id="legal-basis" title="5. Legal basis (GDPR users)">
          <p>For visitors in the European Economic Area, United Kingdom, and Switzerland, we process data under the following legal bases:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Contract</strong> (Art. 6(1)(b)) — for anything we need to do to provide the service you've signed up for.</li>
            <li><strong>Legitimate interests</strong> (Art. 6(1)(f)) — for fraud prevention, security, and minimal product analytics that don't override your rights.</li>
            <li><strong>Consent</strong> (Art. 6(1)(a)) — for the AdSense ads on /blog and the optional analytics cookies. You can withdraw consent anytime via the cookie banner.</li>
            <li><strong>Legal obligation</strong> (Art. 6(1)(c)) — for retaining billing records as required by Indian tax law.</li>
          </ul>
        </Section>

        <Section id="retention" title="6. How long we keep data">
          <p>Different categories of data are retained for different periods:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>File content</strong>: until the transfer expires (1 hour to 7 days), plus up to one hour for the deletion process to complete.</li>
            <li><strong>Transfer metadata</strong>: anonymised after the file is purged; aggregated form retained for analytics.</li>
            <li><strong>Account data</strong>: while your account is active, plus 30 days after deletion for backup rotation.</li>
            <li><strong>Billing records</strong>: 8 years, as required by Indian tax law.</li>
            <li><strong>Hashed IP addresses</strong>: 30 days, for abuse prevention.</li>
            <li><strong>Server logs</strong>: 14 days.</li>
          </ul>
        </Section>

        <Section id="storage" title="7. Where we store data">
          <p>
            File content is stored on encrypted object storage in Asia-South (Mumbai region). Database content (accounts, metadata) is stored in MongoDB Atlas, also Asia-South. Payments are processed by Razorpay in India. SMS OTPs are delivered through MSG91 in India. Google Analytics 4 data is processed by Google globally (typically routed to EU or US data centres based on your location).
          </p>
          <p>
            Cross-border transfers to the EU and US rely on standard contractual clauses with our processors, plus our own data-minimisation practices.
          </p>
        </Section>

        <Section id="security" title="8. How we secure data">
          <p>
            Files are transferred over HTTPS (TLS 1.3) and stored with AES-256-GCM encryption at rest. Account passwords are stored as bcrypt hashes. Transfer passwords are stored as bcrypt hashes. Storage paths are non-guessable UUIDs and never exposed to receivers. Access to production systems is restricted to a small number of named employees with hardware-token authentication. We log all access for audit purposes.
          </p>
          <p>
            For a deeper technical writeup of our security model, see <a className="underline" href="/security">/security</a>.
          </p>
        </Section>

        <Section id="your-rights" title="9. Your rights">
          <p>Depending on where you live, you have some or all of the following rights:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Access</strong>: a copy of the personal data we hold about you.</li>
            <li><strong>Rectification</strong>: correction of inaccurate or incomplete data.</li>
            <li><strong>Erasure</strong>: deletion of your account and associated data ("right to be forgotten").</li>
            <li><strong>Restriction</strong>: limit our processing of your data while a dispute is resolved.</li>
            <li><strong>Portability</strong>: a copy of your data in a structured, machine-readable format.</li>
            <li><strong>Objection</strong>: object to processing based on legitimate interests.</li>
            <li><strong>Withdraw consent</strong>: at any time, for processing that relies on consent.</li>
          </ul>
          <p>
            To exercise any of these rights, write to <a className="underline" href="mailto:privacy@sendme.co.in">privacy@sendme.co.in</a>. We respond within 30 days and don't charge a fee for reasonable requests. You also have the right to complain to your local data-protection authority.
          </p>
        </Section>

        <Section id="cookies" title="10. Cookies and tracking">
          <p>We use a small number of cookies:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Strictly necessary</strong> — to keep you signed in (session cookie, HttpOnly).</li>
            <li><strong>Analytics</strong> — Google Analytics 4 (_ga, _ga_*) to count pageviews. You can opt out via the cookie banner.</li>
            <li><strong>Advertising</strong> — Google AdSense loads only on /blog pages and only after consent. Non-personalised ads by default for EU visitors.</li>
          </ul>
          <p>
            We do not use third-party tracking pixels, marketing-attribution beacons, or session-replay tools.
          </p>
        </Section>

        <Section id="children" title="11. Children's data">
          <p>
            SendMe is not directed to children under 13. The anonymous features can technically be used by anyone with a browser, but account features (sign-up, paid plans, team workspaces) are restricted to users 18 and over. We don't knowingly collect data from children under 13 and will delete any such account on notice.
          </p>
        </Section>

        <Section id="changes" title="12. Changes to this policy">
          <p>
            We may update this policy as the product or applicable laws evolve. The "Last updated" date at the top reflects the most recent change. Material changes will be flagged in-app at least 14 days before they take effect.
          </p>
        </Section>

        <Section id="contact" title="13. Contact us">
          <p>
            Privacy questions, requests, and complaints all go to <a className="underline" href="mailto:privacy@sendme.co.in">privacy@sendme.co.in</a>. For postal mail, contact us through the form at <a className="underline" href="/contact">/contact</a> and we'll share an address suitable for the specific purpose.
          </p>
        </Section>
      </div>
    </PageShell>
  );
}
